import { replaceDateTextWithRanges } from "@services/utils/dateMapper";
import { useState, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getFilterLimit, canAddSelection } from "./filterLimits";
import {
  CombinationsSelections,
  useSelectionMessage
} from "./useSelectionMessage";

export type FlowStep = "day" | "activity" | "location" | "complete";

/**
 * Hook to manage the step-by-step flow for combinations filter
 * Flow: day → activity → location → complete
 * Includes a 5-second delay before advancing to the next step to allow multiple selections
 */
export const useCombinationsFlow = () => {
  const { t, i18n } = useTranslation();
  const { buildRegularFilterMessage } = useSelectionMessage();

  // Helper function to format items list (duplicated from useSelectionMessage for local use)
  const formatItemsList = (items: string[]): string => {
    if (items.length === 0) return "";
    if (items.length === 1) return items[0];

    const lang = i18n.language || "ca";
    const isCa = lang.startsWith("ca");
    const isEs = lang.startsWith("es");
    const conjunction = isCa ? "i" : isEs ? "y" : "and";

    const allButLast = items.slice(0, -1).join(", ");
    const last = items[items.length - 1];
    return `${allButLast} ${conjunction} ${last}`;
  };
  const [selections, setSelections] = useState<CombinationsSelections>({
    activities: [],
    locations: [],
    days: []
  });

  // Displayed step controls which step's pills are shown
  const [displayedStep, setDisplayedStep] = useState<FlowStep>("day");
  // Show pills flag controls if pills are visible (hidden after 3 seconds of no clicks)
  const [showPills, setShowPills] = useState(true);
  // Is waiting flag indicates if we're in the 3-second delay period
  const [isWaiting, setIsWaiting] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSelectionsSnapshot = useRef<string>("");

  // Determine actual current step based on selections
  const actualStep: FlowStep = useMemo(() => {
    if (selections.days.length === 0) return "day";
    if (selections.activities.length === 0) return "activity";
    if (selections.locations.length === 0) return "location";
    return "complete";
  }, [selections]);

  // Handle delay after last click - reset timer on each selection change
  useEffect(() => {
    const currentSelectionsStr = JSON.stringify(selections);

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Check if we're going backwards (removing all selections from current step)
    const goingBackwards =
      (displayedStep === "day" && selections.days.length === 0) ||
      (displayedStep === "activity" &&
        selections.activities.length === 0 &&
        selections.days.length > 0) ||
      (displayedStep === "location" &&
        selections.locations.length === 0 &&
        selections.activities.length > 0);

    if (goingBackwards) {
      // Update immediately when going backwards
      setDisplayedStep(actualStep);
      setShowPills(true);
      setIsWaiting(false);
      lastSelectionsSnapshot.current = currentSelectionsStr;
      return;
    }

    // Check if current step has selections (to start timer)
    const currentStepHasSelections =
      (displayedStep === "day" && selections.days.length > 0) ||
      (displayedStep === "activity" && selections.activities.length > 0) ||
      (displayedStep === "location" && selections.locations.length > 0);

    // If selections changed for the current step
    if (currentSelectionsStr !== lastSelectionsSnapshot.current) {
      // If user deselected all options, stop timer and hide spinner
      if (!currentStepHasSelections) {
        // Clear timer if it exists
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setIsWaiting(false);
        setShowPills(true);
        lastSelectionsSnapshot.current = currentSelectionsStr;
        return;
      }

      // User selected something - show pills and start/reset timer
      setShowPills(true);
      lastSelectionsSnapshot.current = currentSelectionsStr;

      // Skip delay for the last step (location) - don't hide pills or show spinner
      const isLastStep = displayedStep === "location";
      if (isLastStep) {
        setIsWaiting(false);
        return;
      }

      // Show spinner while waiting
      setIsWaiting(true);

      // Wait 3 seconds after last click
      timeoutRef.current = setTimeout(() => {
        // Check if selections changed during delay (another click or deselection happened)
        const finalSelectionsStr = JSON.stringify(selections);
        if (finalSelectionsStr !== currentSelectionsStr) {
          return; // Selections changed, timer was reset, don't update
        }

        // Double-check that we still have selections for current step
        const stillHasSelections =
          (displayedStep === "day" && selections.days.length > 0) ||
          (displayedStep === "activity" && selections.activities.length > 0);

        // Only advance if we still have selections
        if (!stillHasSelections) {
          setIsWaiting(false);
          return;
        }

        // Hide pills for current step and stop waiting
        setShowPills(false);
        setIsWaiting(false);

        // Advance to next step if we can
        if (actualStep !== displayedStep && actualStep !== "complete") {
          setDisplayedStep(actualStep);
          // Show pills for next step immediately
          setShowPills(true);
          // Reset snapshot for next step
          lastSelectionsSnapshot.current = JSON.stringify(selections);
        }
      }, 3000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [selections, actualStep, displayedStep]);

  // Current step for UI
  const currentStep = displayedStep;

  const handleItemSelect = (
    itemKey: string,
    category: "activities" | "locations" | "days"
  ) => {
    setSelections((prev) => {
      const currentItems = prev[category];
      const isSelected = currentItems.includes(itemKey);

      if (isSelected) {
        return {
          ...prev,
          [category]: currentItems.filter((key) => key !== itemKey)
        };
      }

      const limit = getFilterLimit(category);
      if (!canAddSelection(currentItems, limit)) {
        return prev;
      }

      return {
        ...prev,
        [category]: [...currentItems, itemKey]
      };
    });
  };

  const removeItem = (
    itemKey: string,
    category: "activities" | "locations" | "days"
  ) => {
    setSelections((prev) => ({
      ...prev,
      [category]: prev[category].filter((key) => key !== itemKey)
    }));
  };

  const clearAll = () => {
    setSelections({
      activities: [],
      locations: [],
      days: []
    });
  };

  // Build dynamic conversation messages based on current step
  // Only shows messages for steps that have been completed (pills hidden)
  // Uses the same logic as individual filters
  const buildConversationMessages = (): Array<{
    source: "user" | "avatar";
    text: string;
  }> => {
    const messages: Array<{ source: "user" | "avatar"; text: string }> = [];

    // Initial messages
    messages.push({
      source: "user",
      text: t("filter_combinations.user_message")
    });
    messages.push({
      source: "avatar",
      text: t("filter_combinations.bot_message")
    });

    // Step 1: Day selection - only show if we've moved past the day step (pills are hidden)
    // We show the message if we're on activity step or beyond (meaning day step is complete)
    if (selections.days.length > 0 && displayedStep !== "day") {
      const dayMessage = buildRegularFilterMessage(
        "day",
        selections.days,
        "filter_day"
      );

      messages.push({
        source: "user",
        text: dayMessage
      });

      // Format days for bot message
      const daysFormatted = formatItemsList(
        selections.days.map((key) => t(`filter_day.${key}`))
      );

      messages.push({
        source: "avatar",
        text: t("filter_combinations.step1_bot_message", {
          defaultValue:
            "Perfecte! Et busco plans per a {{days}}. Quin pla et ve més de gust? Pots clicar un tipus d'activitat o dir-m'ho directament.",
          days: daysFormatted
        })
      });
    }

    // Step 2: Activity selection - only show if we've moved past the activity step (pills are hidden)
    // We show the message if we're on location step or beyond (meaning activity step is complete)
    if (
      selections.activities.length > 0 &&
      displayedStep !== "activity" &&
      displayedStep !== "day"
    ) {
      const activityMessage = buildRegularFilterMessage(
        "activity",
        selections.activities,
        "filter_section"
      );

      messages.push({
        source: "user",
        text: activityMessage
      });

      // Format days and activities for bot message
      const daysFormatted = formatItemsList(
        selections.days.map((key) => t(`filter_day.${key}`))
      );
      const activitiesFormatted = formatItemsList(
        selections.activities.map((key) => t(`filter_section.${key}`))
      );

      messages.push({
        source: "avatar",
        text: t("filter_combinations.step2_bot_message", {
          defaultValue:
            "Genial! Et busco plans de {{activities}} per {{days}}. On t'aniria bé? Clica \"A prop meu\" per activar la ubicació o tria un barri dels d'aquí sota. Si no hi és, escriu-lo.",
          activities: activitiesFormatted,
          days: daysFormatted
        })
      });
    }

    return messages;
  };

  // Build final message for input using the same logic as buildRegularFilterMessage for activities
  const buildFinalMessage = (): string => {
    const parts: string[] = [];

    // Activities with differentiation for gastronomy/nightlife
    if (selections.activities.length > 0) {
      const localesActivities = ["gastronomy", "nightlife"];
      const regularActivities: string[] = [];
      const localesItems: string[] = [];

      selections.activities.forEach((key) => {
        if (localesActivities.includes(key)) {
          localesItems.push(t(`filter_section.${key}`));
        } else {
          regularActivities.push(t(`filter_section.${key}`));
        }
      });

      if (regularActivities.length > 0) {
        const regularText = formatItemsList(regularActivities);
        parts.push(
          t("filter_section.proposals_part", {
            defaultValue: "propuestas de {{items}}",
            items: regularText
          })
        );
      }

      if (localesItems.length > 0) {
        const localesText = formatItemsList(localesItems);
        parts.push(
          t("filter_section.locales_part", {
            defaultValue: "locales de {{items}}",
            items: localesText
          })
        );
      }
    }

    // Locations
    if (selections.locations.length > 0) {
      const locationsText = formatItemsList(
        selections.locations.map((key) => t(`filter_location.${key}`))
      );
      parts.push(
        t("filter_combinations.locations_part", {
          defaultValue: "en {{items}}",
          items: locationsText
        })
      );
    }

    // Days
    if (selections.days.length > 0) {
      const daysText = formatItemsList(
        selections.days.map((key) => t(`filter_day.${key}`))
      );
      parts.push(
        t("filter_combinations.days_part", {
          defaultValue: "para {{items}}",
          items: daysText
        })
      );
    }

    if (parts.length === 0) return "";

    // Join parts with commas instead of conjunctions
    return t("filter_combinations.combined_message", {
      defaultValue: "Estoy buscando {{parts}}.",
      parts: parts.join(", ")
    });
  };

  /**
   * Transforms the final message by replacing date text with numeric date ranges
   * This should be called before sending the message to the backend
   *
   * @param message - The message to transform
   * @returns Transformed message with date text replaced by numeric ranges
   */
  const transformFinalMessageForBackend = (message: string): string => {
    // Only transform if we have date selections
    if (selections.days.length === 0) {
      return message;
    }

    // Get the translated texts for the selected dates
    const translatedDateTexts = selections.days.map((key) =>
      t(`filter_day.${key}`)
    );

    // Get current language for date formatting
    const lang = i18n.language || "ca";
    const languageCode = lang.substring(0, 2);

    // Replace date text with numeric ranges
    return replaceDateTextWithRanges(
      message,
      selections.days,
      translatedDateTexts,
      languageCode
    );
  };

  return {
    currentStep,
    selections,
    showPills,
    isWaiting,
    handleItemSelect,
    removeItem,
    clearAll,
    buildConversationMessages,
    buildFinalMessage,
    transformFinalMessageForBackend
  };
};
